/**
 * A structured Bible reference (epic #297, #303). A reference is stored as
 * OSIS book codes with chapter and verse bounds, plus a free-text fallback for
 * anything the structure cannot hold. Read a stored value through
 * `coerceScripture` and print it through `formatScripture`.
 */

/** The 66 OSIS book codes of the Protestant canon, in canon order. */
export const BIBLE_BOOK_CODES = [
  'Gen',
  'Exod',
  'Lev',
  'Num',
  'Deut',
  'Josh',
  'Judg',
  'Ruth',
  '1Sam',
  '2Sam',
  '1Kgs',
  '2Kgs',
  '1Chr',
  '2Chr',
  'Ezra',
  'Neh',
  'Esth',
  'Job',
  'Ps',
  'Prov',
  'Eccl',
  'Song',
  'Isa',
  'Jer',
  'Lam',
  'Ezek',
  'Dan',
  'Hos',
  'Joel',
  'Amos',
  'Obad',
  'Jonah',
  'Mic',
  'Nah',
  'Hab',
  'Zeph',
  'Hag',
  'Zech',
  'Mal',
  'Matt',
  'Mark',
  'Luke',
  'John',
  'Acts',
  'Rom',
  '1Cor',
  '2Cor',
  'Gal',
  'Eph',
  'Phil',
  'Col',
  '1Thess',
  '2Thess',
  '1Tim',
  '2Tim',
  'Titus',
  'Phlm',
  'Heb',
  'Jas',
  '1Pet',
  '2Pet',
  '1John',
  '2John',
  '3John',
  'Jude',
  'Rev',
] as const;

/** One OSIS book code, e.g. `Gen`, `1Cor`, `Rev`. */
export type TBibleBook = (typeof BIBLE_BOOK_CODES)[number];

/**
 * One passage. A whole chapter or a chapter range has null verses; a verse
 * range sets both verses. `verseEnd` belongs to `chapterEnd`.
 */
export type TScriptureRef = {
  book: TBibleBook;
  /** Display-name snapshot, e.g. `Romans`. */
  bookName: string;
  chapterStart: number;
  chapterEnd: number;
  verseStart: number | null;
  verseEnd: number | null;
};

/** A stored Bible reference: structured refs, a free-text fallback, and the books it touches. */
export type TScripture = {
  refs: TScriptureRef[];
  /** Free text for what the refs cannot hold (or a legacy string), printed after the refs. */
  freeform: string;
  /** The unique books of `refs`, in canon order. Derived, never trusted on read. */
  books: TBibleBook[];
};
